import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { Figure, PageHeader, useFormats } from '@/pages/bacen/shared';
import { fetchCrsfnBoard } from '@/services/bacen';

const PAGE_SIZE = 50;

/**
 * The CRSFN as the BACEN ledger sees it: which acordaos of the docket are
 * linked to a ledger row (and which are not: CVM, COAF, the old series), the
 * sessions those acordaos sat in, and who sat -- presence counted from the
 * roll call of each dispositive, never a mandate list. Three grains on one
 * page, named on every panel: acordao, session, member.
 */
export function CrsfnBoardPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, percent } = useFormats();
  const [offset, setOffset] = useState(0);
  const { data, isPending, error, refetch, isPlaceholderData } = useQuery({
    queryKey: ['bacen', 'crsfn', 'board', offset],
    queryFn: () => fetchCrsfnBoard({ limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader page="bacen:crsfnBoard" exportPath="/api/v1/bacen/export/crsfn/board" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-population>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnBoard.population.title')}
        </h2>
        <Answer answer={data.population} help={t('bacen:crsfnBoard.population.help')}>
          {(p) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('bacen:crsfnBoard.population.docket')}
                  value={number.format(p.docket_acordaos)}
                />
                <Figure
                  label={t('bacen:crsfnBoard.population.linked')}
                  value={`${number.format(p.linked)} (${percent.format(
                    p.docket_acordaos ? p.linked / p.docket_acordaos : 0,
                  )})`}
                  second={t('bacen:crsfnBoard.population.oldSeries', {
                    n: number.format(p.old_series_linked),
                  })}
                  testId="linked"
                />
                <Figure
                  label={t('bacen:crsfnBoard.population.ledgerRows')}
                  value={number.format(p.ledger_board_rows)}
                  second={t('bacen:crsfnBoard.population.ledgerLinked', {
                    n: number.format(p.ledger_rows_linked),
                  })}
                />
                <Figure
                  label={t('bacen:crsfnBoard.population.unmatched')}
                  value={number.format(p.ledger_rows_unmatched)}
                  second={t('bacen:crsfnBoard.population.keyConflicts', {
                    n: number.format(p.key_conflicts),
                  })}
                  testId="unmatched"
                />
              </dl>
              <p className="text-xs text-muted-foreground" data-origin-hints>
                {t('bacen:crsfnBoard.population.unlinkedBy', {
                  n: number.format(p.unlinked),
                })}{' '}
                {Object.entries(p.by_origin_hint)
                  .map(
                    ([hint, n]) =>
                      `${t(`bacen:codes.originHint.${hint}`, { defaultValue: hint })} ${number.format(n)}`,
                  )
                  .join(' · ')}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:crsfnBoard.guide.population" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-roster>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnBoard.roster.title')}
        </h2>
        <Answer answer={data.roster} help={t('bacen:crsfnBoard.roster.help')}>
          {(r) => (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('bacen:crsfnBoard.roster.summary', {
                  people: number.format(r.people),
                  rollCall: number.format(r.acordaos_with_roll_call),
                  linked: number.format(r.acordaos_linked),
                })}
              </p>
              <div className="max-h-96 overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:crsfnBoard.roster.member')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnBoard.roster.participations')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnBoard.roster.asRelator')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnBoard.roster.asVencido')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnBoard.roster.years')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.members.map((m) => (
                      <tr key={m.slug} className="border-t" data-member={m.slug}>
                        <td className="px-3 py-2 text-foreground">
                          {m.as_relator > 0 ? (
                            <Link
                              to={`/bacen/crsfn/r/${m.slug}`}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              {m.name}
                            </Link>
                          ) : (
                            m.name
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(m.participations)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(m.as_relator)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(m.as_vencido)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {m.first_year ?? '—'}–{m.last_year ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:crsfnBoard.guide.roster" />
        </div>
      </section>

      <section
        className="rounded-lg border bg-card p-4"
        data-sessions
        data-stale={isPlaceholderData || undefined}
      >
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnBoard.sessions.title')}
        </h2>
        <Answer answer={data.sessions} help={t('bacen:crsfnBoard.sessions.help')}>
          {(s) => (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground" data-total={data.total_sessions}>
                {t('bacen:crsfnBoard.sessions.summary', {
                  total: number.format(data.total_sessions),
                  withDates: number.format(s.with_dates),
                  withRollCall: number.format(s.with_roll_call),
                })}
              </p>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:crsfnBoard.sessions.session')}
                      </th>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:crsfnBoard.sessions.dates')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnBoard.sessions.acordaos')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnBoard.sessions.linked')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnBoard.sessions.withRollCall')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.sessions.map((row) => (
                      <tr
                        key={row.session_number}
                        className="border-t"
                        data-session={row.session_number}
                      >
                        <td className="px-3 py-2 text-foreground">{row.session_raw}</td>
                        <td className="px-3 py-2 tabular-nums text-muted-foreground">
                          {row.first_date ?? '—'}
                          {row.last_date && row.last_date !== row.first_date
                            ? ` → ${row.last_date}`
                            : null}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(row.acordaos)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(row.linked)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(row.with_roll_call)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.total_sessions > PAGE_SIZE ? (
                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    className="text-primary underline-offset-2 hover:underline disabled:text-muted-foreground"
                  >
                    {t('bacen:consistency.pairs.previous')}
                  </button>
                  <span className="text-muted-foreground">
                    {t('bacen:consistency.pairs.page', {
                      from: number.format(offset + 1),
                      to: number.format(Math.min(offset + PAGE_SIZE, data.total_sessions)),
                    })}
                  </span>
                  <button
                    type="button"
                    disabled={offset + PAGE_SIZE >= data.total_sessions}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    className="text-primary underline-offset-2 hover:underline disabled:text-muted-foreground"
                  >
                    {t('bacen:consistency.pairs.next')}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </Answer>
      </section>
    </div>
  );
}
